import React, { useCallback, useEffect, useRef, useState } from 'react';
import streamingClientsService from '../../services/streamingClientsService';
import './StreamingClients.css';

// ONE STREAMING CLIENT'S CONTAINER LOG, with a tail. The first read takes the last N lines; while Tail is ticked it asks
// every 3 seconds for the lines since the newest one it has (Kubernetes' own per-line timestamp), so nothing is
// repeated or skipped. Previous instance = the log of the container before its last restart — why it crashed.
// Used by Admin › Infra › Streaming Clients and by the pipeline board's Deploy Streaming Client dialog.

const TAIL_MS = 3000;
const MAX_LINES = 5000;
// Kubernetes' RFC 3339 nanosecond stamps drop trailing zeros, so they only sort as strings once padded to 9 digits.
const norm = (ts) => (ts ? ts.replace(/(?:\.(\d+))?Z$/, (_, f) => `.${(f || '').padEnd(9, '0')}Z`) : '');
const errText = (e) => e.response?.data?.error?.message || e.response?.data?.message || e.message;

export default function StreamingLogViewer({ clientKey }) {
    const [lines, setLines] = useState([]);
    const [tailLines, setTailLines] = useState(200);
    const [previous, setPrevious] = useState(false);
    const [tail, setTail] = useState(false);
    const [pod, setPod] = useState(null);
    const [note, setNote] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const inFlight = useRef(false);
    const lastTs = useRef(null);
    const box = useRef(null);
    const pinned = useRef(true);   // scrolled to the bottom: keep following new lines

    const load = useCallback(async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setLoading(true);
        try {
            const data = (await streamingClientsService.logs(clientKey, { tailLines, previous: previous || undefined })).data;
            setLines(data.lines || []);
            lastTs.current = (data.lines || []).length ? data.lines[data.lines.length - 1].ts : null;
            setPod(data.pod || null);
            setNote(data.note || null);
            setError(null);
            pinned.current = true;
        } catch (e) {
            setError(errText(e));
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }, [clientKey, tailLines, previous]);

    useEffect(() => { load(); }, [load]);

    // A FOLLOW-UP READ: only what is newer than the last line held. sinceTime is second-precision at the API, so the
    // answer starts at the same second; lines at or before the last timestamp are dropped here.
    const more = useCallback(async () => {
        if (inFlight.current || !lastTs.current) { if (!lastTs.current) load(); return; }
        inFlight.current = true;
        try {
            const since = lastTs.current;
            const data = (await streamingClientsService.logs(clientKey, { sinceTime: since.replace(/\.\d+Z$/, 'Z') })).data;
            const fresh = (data.lines || []).filter(l => !l.ts || norm(l.ts) > norm(since));
            if (data.pod && data.pod !== pod) {
                // A new pod (a redeploy, a restart): start again from its log.
                inFlight.current = false;
                return load();
            }
            if (fresh.length) {
                lastTs.current = fresh[fresh.length - 1].ts || lastTs.current;
                setLines(ls => [...ls, ...fresh].slice(-MAX_LINES));
            }
            setError(null);
        } catch (e) {
            setError(errText(e));
        } finally {
            inFlight.current = false;
        }
    }, [clientKey, pod, load]);

    useEffect(() => {
        if (!tail || previous) return undefined;
        const id = setInterval(more, TAIL_MS);
        return () => clearInterval(id);
    }, [tail, previous, more]);

    useEffect(() => {
        if (pinned.current && box.current) box.current.scrollTop = box.current.scrollHeight;
    }, [lines]);

    const onScroll = () => {
        const el = box.current;
        if (el) pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    };

    return (
        <div className="stc-log">
            <div className="stc-log-bar">
                <span className="scq-meta">{pod ? `Pod ${pod}` : 'No pod'}</span>
                <label>Lines
                    <select className="odq-filter-select" value={tailLines} onChange={e => setTailLines(Number(e.target.value))}>
                        {[100, 200, 500, 1000, 2000].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                </label>
                <label><input type="checkbox" checked={previous} onChange={e => { setPrevious(e.target.checked); setTail(false); }} /> Previous instance</label>
                <label><input type="checkbox" checked={tail} disabled={previous} onChange={e => setTail(e.target.checked)} /> Tail (3s)</label>
                <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={load} disabled={loading}>Reload</button>
            </div>
            {error && <div className="os-msg os-msg-error">{error}</div>}
            <pre className="stc-log-pre" ref={box} onScroll={onScroll}>
                {lines.length
                    ? lines.map((l, i) => (
                        <div key={`${l.ts}-${i}`} className={/\[(ERROR|FATAL)\]/.test(l.text) ? 'stc-log-err' : /\[WARN\]/.test(l.text) ? 'stc-log-warn' : undefined}>
                            <span className="stc-log-ts">{l.ts ? new Date(l.ts).toLocaleTimeString() : ''}</span> {l.text}
                        </div>))
                    : (loading ? 'Loading…' : (note || 'No log lines.'))}
            </pre>
        </div>
    );
}
